import { AgGridReactProps } from '@ag-grid-community/react';
import { Grid } from '@mui/material';
import {
    ActionTypes,
    AdminRoleId,
    ClientWalletRolesMap,
    SelectOption,
    doesWorkflowRejectCodeExist
} from '@tassat/common-definitions';
import { StyledSelect } from '@tassat/formik-input';
import { UserAccountMapped } from '@tassat/fred-extended-definitions';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';
import { SingleValue } from 'react-select';

import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { useAppSelector } from 'Reducers/store/hooks';
import { getUserAdminRoleId } from 'Selectors/common';
import { getFirmByFirmId } from 'Selectors/firms';

interface UserAccountRoleCellRendererProps extends AgGridReactProps {
    value: {
        ClientRoleId: string;
    };
    data: UserAccountMapped;
    firmId: string;
}

const UserAccountRoleCellRenderer = (props: UserAccountRoleCellRendererProps) => {
    const {
        value,
        data,
        firmId,
        context
    } = props;

    const firm = useAppSelector((state) => getFirmByFirmId(state, firmId));
    const userRole = useAppSelector(getUserAdminRoleId);
    const displayRole = ClientWalletRolesMap.filter(role => role.id === value.ClientRoleId)[0];

    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const ClientRoles: SelectOption[] = ClientWalletRolesMap.map(role => {
        return {
            label: role.displayName,
            value: role.id,
            refData: role
        };
    });
    // const [selectedRole] = React.useState<SelectOption>(ClientRoles.find(role => role.value === value.ClientRoleId));
    const selectedRole: SelectOption = React.useMemo(() => (
        ClientRoles.find(role => role.value === value.ClientRoleId)
    ), [ ClientRoles, value.ClientRoleId ]);
    const allowedRoles: SelectOption[] = data.account.AccessControl ?
        ClientRoles.filter(role => role.refData.label !== 'F')
        : ClientRoles.filter(role => role.refData.label !== 'M' && role.refData.label !== 'A');

    const handleChange = (selectedOption: SingleValue<SelectOption>) => {
        if (selectedOption?.value === selectedRole.value) {
            return;
        } else {
            changeUserRoleValidation({
                userAccount: data,
                newRole: selectedOption
            });
        }
    };

    const customStyles = {
        container: (base) => ({
            ...base,
            padding: '.25rem'
        }),
        control: (base) => ({
            ...base,
            height: '24px',
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent'
        }),
        valueContainer: (base) => ({
            ...base,
            height: '24px',
            width: '7rem',
            fontSize: '12px'
        }),
        menu: (base) => ({
            ...base,
            margin: '1px'
        }),
        indicatorSeparator: (base) => ({
            ...base,
            display: 'none'
        })
    };

    const changeUserRoleValidation = async (data: { userAccount: UserAccountMapped; newRole: SingleValue<SelectOption> }) => {
        const {
            userAccount,
            newRole
        } = data;

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: {
                    userAccount,
                    newRole
                },
                actionType: ActionTypes.edit,
                confirmTitle: `Confirm Editing Role for User ${userAccount.user.Email}`,
                confirmBody: `Please confirm changing role for ${userAccount.user.Email} from ${selectedRole.refData.displayName} to ${newRole?.refData.displayName}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const changeUserRole = async (data: { userAccount: UserAccountMapped; newRole: SingleValue<SelectOption> }) => {
        const {
            userAccount,
            newRole
        } = data;

        try {
            const response = await Worker.getInstance()
                .updateUserAccount({
                    UserAccountId: userAccount.userAccount.UserAccountId,
                    UserId: userAccount.userAccount.UserId,
                    AccountId: userAccount.userAccount.AccountId,
                    ClientRoleId: newRole?.value,
                    WireRoleId: userAccount.userAccount.WireRoleId,
                    IsFavorite: userAccount.userAccount.IsFavorite,
                    IsDeleted: userAccount.userAccount.IsDeleted
                });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Changed Role for ${userAccount.user.Email}`,
                        successBody: `Successfully changed role for ${userAccount.user.Email} from ${selectedRole.refData.displayName} to ${newRole?.refData.displayName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Change Role for ${userAccount.user.Email}`,
                        failureBody: `Could not change role for ${userAccount.user.Email} from ${selectedRole.refData.displayName} to ${newRole?.refData.displayName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Change Role for ${userAccount.user.Email}`,
                    failureBody: `Something went wrong changing role for ${userAccount.user.Email} from ${selectedRole.refData.displayName} to ${newRole?.refData.displayName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: data,
                setSelectedItem: () => data,
                preActionValidationAndConfig: { [ActionTypes.edit]: changeUserRoleValidation },
                onConfirm: { [ActionTypes.edit]: changeUserRole },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                alignItems='flex-start'
                container
                direction='column'
                justifyContent='center'
                spacing={0}
                wrap='nowrap'
            >
                {(!data.account.IsClosed && !firm?.IsClosed && userRole !== AdminRoleId.BANK_OPS_SUPERVISOR &&
                    userRole !== AdminRoleId.PCG_ADMIN) ?
                        <StyledSelect
                            customStyles={customStyles}
                            id='userRoleSelect'
                            isSearchable={false}
                            menuPortalTarget={document.querySelector('body')}
                            onChange={handleChange}
                            options={allowedRoles}
                            value={allowedRoles.find(role => role.value === selectedRole?.value)}
                        />
                    : <span>{displayRole.displayName}</span>}
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
};

export default UserAccountRoleCellRenderer;